import React, { useState } from 'react';
import { uploadLectureSolvedImage, deleteLectureSolvedImage } from '../../utils/lectureStorage';
import SafeImage from '../SafeImage';
import {
  emptyLectureSection,
  foldLectureBlocks,
  importLectureJson,
  newLectureBlockId,
  sampleLectureJsonText,
  sectionImages,
} from '../../utils/lectureContent';

export default function LectureEditor({
  blocks,
  onChange,
  moduleId,
  dayId,
  accentColor = '#1b365d',
}) {
  const [jsonError, setJsonError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const sections = foldLectureBlocks(blocks);

  function setBlocks(next) {
    onChange(typeof next === 'function' ? next(sections) : next);
  }

  function updateBlock(index, patch) {
    setBlocks(sections.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  }

  function moveBlock(index, dir) {
    const next = index + dir;
    if (next < 0 || next >= sections.length) return;
    const copy = [...sections];
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item);
    setBlocks(copy);
  }

  async function removeBlock(index) {
    const block = sections[index];
    await Promise.all(
      sectionImages(block).map((img) => deleteLectureSolvedImage(img.imagePath || img.path))
    );
    setBlocks(sections.filter((_, i) => i !== index));
  }

  function addSection() {
    setBlocks([...sections, emptyLectureSection(`Section ${sections.length + 1}`)]);
  }

  async function addImagesToSection(index, fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!moduleId) {
      setUploadError('Open a module first, then upload pictures on each section.');
      return;
    }
    setUploadError('');
    setUploadingIndex(index);
    try {
      const uploaded = [];
      for (const file of files) {
        const { url, path } = await uploadLectureSolvedImage(file, moduleId, dayId || 'draft');
        uploaded.push({
          id: newLectureBlockId(),
          imageUrl: url,
          url,
          imagePath: path,
          path,
          alt: '',
          description: '',
        });
      }
      const current = foldLectureBlocks(blocks);
      const next = current.map((b, i) =>
        i === index ? { ...b, images: [...sectionImages(b), ...uploaded] } : b
      );
      onChange(next);
    } catch (err) {
      const code = err?.code ? ` (${err.code})` : '';
      setUploadError(err.message || `Image upload failed${code}`);
      alert(err.message || 'Image upload failed');
    } finally {
      setUploadingIndex(null);
    }
  }

  async function removeImage(sectionIndex, imageId) {
    const block = sections[sectionIndex];
    const images = sectionImages(block);
    const target = images.find((img) => img.id === imageId);
    if (target?.imagePath) await deleteLectureSolvedImage(target.imagePath);
    updateBlock(sectionIndex, { images: images.filter((img) => img.id !== imageId) });
  }

  function updateImage(sectionIndex, imageId, patch) {
    const block = sections[sectionIndex];
    updateBlock(sectionIndex, {
      images: sectionImages(block).map((img) => (img.id === imageId ? { ...img, ...patch } : img)),
    });
  }

  function handleJsonFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = importLectureJson(String(reader.result || ''));
        setJsonError('');
        onChange(imported.blocks, {
          title: imported.title,
          source: 'json',
          youtubeVideos: imported.youtubeVideos,
        });
      } catch (err) {
        setJsonError(err.message || 'Could not read this JSON file.');
      }
    };
    reader.onerror = () => setJsonError('Could not read this JSON file.');
    reader.readAsText(file);
  }

  function downloadSample() {
    const blob = new Blob([sampleLectureJsonText()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lecture-sample.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div style={st.toolbar}>
        <div>
          <label style={st.formLabel}>Lecture content</label>
          <p style={st.hint}>
            Write each section, then upload one or more pictures for that section. They appear in a
            row under the text. Images are uploaded files only — no image links. Math: $inline$ or
            $$display$$.
          </p>
        </div>
        <div style={st.toolbarBtns}>
          <button type="button" onClick={downloadSample} style={st.ghostBtn}>
            Sample JSON
          </button>
          <label style={{ ...st.ghostBtn, cursor: 'pointer' }}>
            Upload JSON
            <input type="file" accept="application/json,.json" onChange={handleJsonFile} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      {jsonError ? <p style={st.error}>{jsonError}</p> : null}
      {uploadError ? <p style={st.error}>{uploadError}</p> : null}

      {sections.map((block, index) => {
        const images = sectionImages(block);
        return (
          <div key={block.id || index} style={st.card}>
            <div style={st.cardHead}>
              <strong style={{ fontSize: 13 }}>Section {index + 1}</strong>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" style={st.iconBtn} onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                  ↑
                </button>
                <button
                  type="button"
                  style={st.iconBtn}
                  onClick={() => moveBlock(index, 1)}
                  disabled={index === sections.length - 1}
                >
                  ↓
                </button>
                <button type="button" style={{ ...st.iconBtn, color: '#dc2626' }} onClick={() => removeBlock(index)}>
                  Delete
                </button>
              </div>
            </div>

            <label style={st.formLabel}>Heading</label>
            <input
              style={st.input}
              value={block.heading || ''}
              onChange={(e) => updateBlock(index, { heading: e.target.value })}
              placeholder="e.g. Introduction"
            />
            <label style={{ ...st.formLabel, marginTop: 10 }}>Body</label>
            <textarea
              style={st.textarea}
              rows={6}
              value={block.text || ''}
              onChange={(e) => updateBlock(index, { text: e.target.value })}
              placeholder={'Write the lecture for this section.\n\nStep 1: …\n$$x + 3 = 7$$'}
            />

            <label style={{ ...st.formLabel, marginTop: 12 }}>Section images</label>
            <p style={{ ...st.hint, margin: '0 0 8px' }}>
              Upload pictures for this section, then write a tutor description under each one. The AI
              tutor uses that text to explain the figure to the student.
            </p>
            {images.length ? (
              <div style={st.imageRow}>
                {images.map((img, imgIndex) => (
                  <div key={img.id} style={st.imageCell}>
                    <SafeImage src={img.imageUrl || img.url} alt={img.alt || ''} style={st.preview} />
                    <label style={{ ...st.formLabel, marginTop: 8 }}>
                      Tutor description {imgIndex + 1}
                    </label>
                    <textarea
                      style={{ ...st.textarea, minHeight: 88, fontSize: 13 }}
                      rows={4}
                      value={img.description || ''}
                      onChange={(e) => updateImage(index, img.id, { description: e.target.value })}
                      placeholder="Describe everything important in this picture so the tutor can teach from it."
                    />
                    <button type="button" style={st.removeImg} onClick={() => removeImage(index, img.id)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ ...st.hint, margin: '0 0 8px' }}>No pictures on this section yet.</p>
            )}
            <div style={st.uploadWrap}>
              <span>{uploadingIndex === index ? 'Uploading…' : 'Upload images'}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif,.heic"
                multiple
                disabled={uploadingIndex != null}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = '';
                  if (files.length) addImagesToSection(index, files);
                }}
                style={st.fileInput}
              />
            </div>
          </div>
        );
      })}

      <button type="button" onClick={addSection} style={{ ...st.addBtn, background: accentColor }}>
        Add section
      </button>
    </div>
  );
}

const st = {
  formLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
  hint: { fontSize: 12, color: '#64748b', lineHeight: 1.45, margin: '0 0 8px' },
  error: {
    fontSize: 13,
    color: '#b91c1c',
    background: '#fef2f2',
    borderRadius: 8,
    padding: '8px 10px',
    marginBottom: 12,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  toolbarBtns: { display: 'flex', gap: 8, flexShrink: 0 },
  ghostBtn: {
    fontSize: 12,
    fontWeight: 700,
    color: '#334155',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: 8,
    padding: '8px 10px',
  },
  uploadWrap: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: '#0f766e',
    borderRadius: 8,
    padding: '10px 14px',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  fileInput: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
    fontSize: 24,
  },
  card: {
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    background: '#fff',
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  iconBtn: {
    fontSize: 12,
    fontWeight: 700,
    border: 'none',
    background: '#f8fafc',
    padding: '6px 8px',
    borderRadius: 8,
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    lineHeight: 1.5,
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  imageRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 10,
  },
  imageCell: {
    flex: '1 1 220px',
    minWidth: 200,
    maxWidth: 320,
  },
  preview: {
    width: '100%',
    height: 120,
    objectFit: 'contain',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    display: 'block',
  },
  removeImg: {
    width: '100%',
    marginTop: 6,
    fontSize: 11,
    fontWeight: 700,
    color: '#dc2626',
    border: 'none',
    background: '#fef2f2',
    borderRadius: 8,
    padding: '6px 8px',
    cursor: 'pointer',
  },
  addBtn: {
    border: 'none',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
  },
};
